/**
 * @charisol-spark/billing — plans, allowances, pricing and cost.
 *
 * Backend enforcement, the in-app upgrade page, the public marketing site and
 * cost measurement all read this package. Nothing else may define a tier, a
 * ceiling, an advertised figure or a provider rate.
 *
 * Every number is in `config.ts`. This file and `cost.ts` hold logic that
 * reads those numbers; they declare none of their own.
 *
 * ## Why one package
 *
 * The same plan numbers used to live in four places — core's PLAN_LIMITS, a
 * PLANS array in the app, another on the marketing site, and a comparison
 * table typed a fourth time — and had drifted where users could see it. The
 * model price table was duplicated in the AI service and in each provider,
 * and had gone stale. One definition removes both classes of bug.
 *
 * ## The rule
 *
 * Numbers are data; the strings shown to users are DERIVED from them. "100 AI
 * generations / month" is generated from `limits.monthlyGenerations`, so a
 * pricing page cannot promise a ceiling the API will refuse.
 *
 * ## Constraint
 *
 * Zero dependencies, imports nothing outside this package. It is bundled into
 * the browser, so anything server-only reaching it would ship AWS SDK code to
 * visitors of the pricing page.
 */

import { planFor } from "./catalogue";
import { STANDARD_POST_CREDITS } from "./config";
import {
  isUnlimited,
  type Limit,
  type Plan,
  type PlanFeature,
  type SubscriptionTier,
  TIER_ORDER,
  UNLIMITED,
} from "./types";

export * from "./types";
export {
  CREDIT_MICRO_USD,
  CREDIT_PACKS,
  CUSTOM_PRICE_CAP_USD,
  DUNNING,
  CHAT_ASK_CREDITS,
  CHAT_EDIT_CREDITS,
  CHAT_EDIT_EXTRA_TAB_CREDITS,
  DEFAULT_AVATAR_IMAGE_MODEL,
  DEFAULT_IMAGE_MODEL,
  ENFORCEMENT,
  IMAGE_CREDITS,
  AVATAR_CREDITS,
  IMAGE_DIRECTIONS_CREDITS,
  PERSONA_HARD_CAP,
  PIECE_OUTLINE_CREDITS,
  PIECE_SECTION_BASE_WORDS,
  PIECE_SECTION_CREDITS,
  PIECE_SECTION_EXTRA_CREDITS,
  PIECE_SECTION_EXTRA_WORDS,
  PIECE_SEO_CREDITS,
  pieceSectionCredits,
  PLANS,
  RATES,
  REGIONAL_MONTHLY_USD,
  REGIONAL_MULTIPLIER,
  STANDARD_POST_CREDITS,
  SUGGESTION_CREDITS,
  TASK_DIGEST_CREDITS,
  TRIAL_ENDING_NOTICE_DAYS,
} from "./config";
export * from "./catalogue";
export * from "./cost";
export * from "./enforcement";
export * from "./margin";
export * from "./projection";
export * from "./payments";

// ── Derived display ──────────────────────────────────────────────────────────

/** A limit as a table cell: `"500"` or `"Unlimited"`. */
export function formatLimit(limit: Limit): string {
  return isUnlimited(limit) ? "Unlimited" : String(limit);
}

/**
 * A credit balance as "about N posts" — the figure copy uses so a limit
 * denominated in credits still reads as something concrete. Floored, since
 * "about" must never promise more posts than the balance actually covers.
 */
export function approximatePosts(credits: Limit): Limit {
  return isUnlimited(credits)
    ? UNLIMITED
    : Math.floor(credits / STANDARD_POST_CREDITS);
}

/**
 * The generations bullet, generated rather than written — the sentence and the
 * enforcement read the same number.
 *
 * Takes a post-equivalent count (see `approximatePosts`), not a raw credit
 * figure: the plan is metered in credits, but the bullet still reads "N AI
 * generations / month" so the marketing copy this feeds does not need to
 * change shape for one release.
 */
export function generationsLabel(limit: Limit): string {
  return isUnlimited(limit)
    ? "Unlimited generations"
    : `${limit} AI generations / month`;
}

/**
 * The X bullet. The number is a guarantee counted at the with-URL price, so
 * the wording promises a floor ("at least" is implied by "posts") and says
 * plainly that posts without links go further.
 */
export function xPostsLabel(limit: Limit): string {
  if (isUnlimited(limit)) return "Unlimited X posts";
  if (limit === 0) return "No X posts";
  return `${limit} X post${limit === 1 ? "" : "s"} / month`;
}

export function personasLabel(limit: Limit): string {
  if (isUnlimited(limit)) return "Unlimited personas";
  return limit === 1 ? "1 Persona" : `${limit} Personas`;
}

/**
 * The full feature list for a plan: derived numeric bullets first, then the
 * hand-written ones. Both pricing surfaces render exactly this.
 */
export function planFeatures(plan: Plan): PlanFeature[] {
  return [
    { label: personasLabel(plan.limits.personas), included: true },
    {
      label: generationsLabel(approximatePosts(plan.limits.monthlyCredits)),
      included: true,
    },
    {
      label: xPostsLabel(plan.limits.monthlyXPosts),
      included: plan.limits.monthlyXPosts !== 0,
    },
    ...plan.extraFeatures,
  ];
}

/** A row of the marketing comparison table, one cell per tier. */
export interface ComparisonRow {
  readonly feature: string;
  readonly hint: string;
  readonly cells: Readonly<Record<SubscriptionTier, string | boolean>>;
}

function cellsFrom(
  pick: (plan: Plan) => string | boolean
): Record<SubscriptionTier, string | boolean> {
  return Object.fromEntries(
    TIER_ORDER.map((t) => [t, pick(planFor(t))])
  ) as Record<SubscriptionTier, string | boolean>;
}

/**
 * The numeric comparison rows, derived from limits. Purely qualitative rows
 * (platforms, editor features) stay in the page — they are copy, not
 * configuration.
 */
export const NUMERIC_COMPARISON_ROWS: readonly ComparisonRow[] = [
  {
    feature: "Personas",
    hint: "Custom author voices that shape tone, style and vocabulary of AI-generated content.",
    cells: cellsFrom((p) => formatLimit(p.limits.personas)),
  },
  {
    feature: "AI generations / mo",
    hint: "Number of AI-powered content transformations you can run each billing month.",
    cells: cellsFrom((p) =>
      formatLimit(approximatePosts(p.limits.monthlyCredits))
    ),
  },
  {
    feature: "X posts / mo",
    hint: "Posts you can publish to X each month, guaranteed even if every post includes a link. Posts without links use less of your allowance, so you can often publish more.",
    cells: cellsFrom((p) => formatLimit(p.limits.monthlyXPosts)),
  },
] as const;

// ── Effective ceiling (base + earned bonus) ──────────────────────────────────

/** A `bonusGenerations` value clamped the same way in every ceiling below. */
function clampedBonus(bonusGenerations: number | undefined): number {
  return typeof bonusGenerations === "number" &&
    Number.isFinite(bonusGenerations)
    ? Math.max(0, Math.floor(bonusGenerations))
    : 0;
}

/**
 * The credit ceiling actually applied to a user: their tier's base plus any
 * streak bonus they have earned, converted to credits at `STANDARD_POST_CREDITS`
 * per bonus unit (2026-09-23: the streak system still speaks in "bonus
 * generations"; this is where that unit becomes the metered one).
 *
 * Here rather than in core so the UI shows the same arithmetic the API
 * enforces, without importing server code.
 *
 * The bonus is clamped, not trusted. It is persisted on the user profile; a
 * negative or non-finite value there would otherwise silently LOWER a paying
 * user's ceiling.
 */
export function effectiveCreditLimit(
  tier: SubscriptionTier,
  bonusGenerations: number | undefined
): Limit {
  const base = planFor(tier).limits.monthlyCredits;
  if (isUnlimited(base)) return UNLIMITED;
  return base + clampedBonus(bonusGenerations) * STANDARD_POST_CREDITS;
}

/**
 * The ceiling actually applied to a user, in the old generation-count unit.
 *
 * @deprecated Derived from `effectiveCreditLimit` for the one-release
 * deprecation window on the `GET /usage` top-level fields; new code should
 * read `effectiveCreditLimit` directly. Kept under its original name and
 * signature so packages that have not migrated yet (the in-app upgrade page,
 * until its own PR lands) keep compiling.
 */
export function effectiveGenerationLimit(
  tier: SubscriptionTier,
  bonusGenerations: number | undefined
): Limit {
  return approximatePosts(effectiveCreditLimit(tier, bonusGenerations));
}
